import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Globe2, Edit3, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", name_en: "", name_ar: "", activo: true };

export default function Countries() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/countries").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditingId(c.id); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.patch(`/countries/${editingId}`, form);
      else await api.post("/countries", form);
      toast.success(t("saved"));
      setShowForm(false); setEditingId(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/countries/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const filtered = rows.filter((c) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [c.code, c.name, c.name_en, c.name_ar].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100";

  return (
    <div className="space-y-4" data-testid="countries-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-red-600" /> {t("countries")}
          </h2>
          <p className="text-sm text-slate-500">{filtered.length} / {rows.length}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")+"..."} className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 w-64" />
          </div>
          <button onClick={openNew} data-testid="new-country-btn" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("new_country")}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">ISO</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Español</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">English</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">العربية</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("status")}</th>
              <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`country-${c.code}`}>
                <td className="p-3 font-mono font-bold text-slate-700">{c.code}</td>
                <td className="p-3">{c.name}</td>
                <td className="p-3 text-slate-600">{c.name_en || "—"}</td>
                <td className="p-3 text-slate-600">{c.name_ar || "—"}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${c.activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {c.activo ? t("active") : t("inactive")}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(c)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`edit-country-${c.code}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-6 space-y-3" data-testid="country-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{editingId ? t("edit") : t("new_country")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-1">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">ISO2</label>
                <input required maxLength={2} data-testid="cf-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inp + " uppercase text-center font-mono font-bold"} />
              </div>
              <div className="col-span-3">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">Español *</label>
                <input required data-testid="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
              </div>
              <div className="col-span-4">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">English</label>
                <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={inp} />
              </div>
              <div className="col-span-4">
                <label className="text-[11px] font-semibold text-slate-600 uppercase mb-1 block">العربية</label>
                <input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} className={inp} dir="rtl" />
              </div>
              <label className="col-span-4 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
                {t("active")}
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="cf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
